import React, { useRef } from "react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { ArrowRight, Check, UserRound, X } from "lucide-react";
import {
  SECRETARY_CAPABILITIES,
  SECRETARY_HEADLINE,
  SECRETARY_HIRED,
  SECRETARY_PAYOFF,
  SECRETARY_REJECTED,
  SECRETARY_SCAN_LINE,
  SECRETARY_STAMP,
  SECRETARY_SUBHEAD,
  SECRETARY_WORKFLOW,
  SOFT_CTA,
} from "./secretaryContent";

const SideList = ({ data, variant }) => {
  const isRejected = variant === "rejected";

  return (
    <div
      className={`rounded-xl border p-4 sm:p-5 h-full ${
        isRejected
          ? "border-red-500/15 bg-red-500/[0.03]"
          : "border-orange-500/25 bg-orange-500/[0.04]"
      }`}
      data-testid={isRejected ? "secretary-contrast-old" : "secretary-contrast-new"}
    >
      <p
        className={`badge-mono text-[10px] font-bold tracking-[0.25em] mb-4 ${
          isRejected ? "text-red-400/70" : "text-orange-400"
        }`}
      >
        {data.label}
      </p>
      <ul className="space-y-3">
        {data.items.map((item, index) => (
          <li key={index} className="flex items-start gap-2.5">
            {isRejected ? (
              <X className="w-4 h-4 text-red-400/60 flex-shrink-0 mt-0.5" aria-hidden="true" />
            ) : (
              <Check className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
            )}
            <span
              className={`text-[11px] sm:text-xs font-body leading-relaxed ${
                isRejected ? "text-gray-600 line-through decoration-red-500/30" : "text-gray-300"
              }`}
            >
              {item}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const SecretarySection = ({ onOpenSignup }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const reduceMotion = useReducedMotion();

  return (
    <section
      className="relative py-14 sm:py-16 lg:py-20 px-6 bg-[#0a0c0f] overflow-hidden"
      data-testid="secretary-section"
      ref={ref}
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_50%_80%_at_50%_50%,rgba(249,115,22,0.06),transparent_70%)] pointer-events-none" />

      <div className="max-w-6xl mx-auto relative">
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className="badge-mono text-[10px] font-bold tracking-[0.28em] text-orange-400/90 uppercase">
            {SECRETARY_STAMP}
          </span>
          <span className="relative flex h-2 w-2" aria-hidden="true">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-orange-400 opacity-50" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-orange-400" />
          </span>
        </div>

        <p
          className="text-sm sm:text-base font-mono text-gray-500 mb-3 tracking-wide"
          data-testid="secretary-scan-line"
        >
          {SECRETARY_SCAN_LINE}
        </p>

        <h2
          className="font-heading font-bold text-2xl sm:text-3xl lg:text-4xl text-[#F3F4F6] leading-[1.1] mb-4 max-w-3xl"
          data-testid="secretary-headline"
        >
          {SECRETARY_HEADLINE.lead}{" "}
          <span className="text-orange-400">{SECRETARY_HEADLINE.accent}</span>{" "}
          {SECRETARY_HEADLINE.tail}
        </h2>

        <p className="font-body text-sm sm:text-base text-[#9CA3AF] mb-10 max-w-2xl leading-relaxed">
          {SECRETARY_SUBHEAD}
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-6 items-stretch mb-10">
          <div className="hidden lg:block lg:col-span-3">
            <SideList data={SECRETARY_REJECTED} variant="rejected" />
          </div>

          <motion.div
            className="lg:col-span-6 order-first lg:order-none"
            initial={reduceMotion ? false : { opacity: 0, scale: 0.97 }}
            animate={reduceMotion || isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.97 }}
            transition={{ duration: 0.45 }}
            data-testid="secretary-employee-pod"
          >
            <div className="relative rounded-2xl border border-orange-500/30 bg-gradient-to-b from-orange-500/10 via-[#0c1018] to-[#0a0c0f] p-6 sm:p-8 shadow-xl shadow-orange-500/5 h-full">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 badge-mono text-[9px] text-orange-400 bg-[#0a0c0f] border border-orange-500/35 px-3 py-0.5 rounded-full whitespace-nowrap">
                DIGITAL HIRE
              </div>

              <div className="flex flex-col items-center text-center mb-6 sm:mb-8">
                <div className="relative mb-4">
                  <div className="absolute inset-0 rounded-full bg-orange-500/20 blur-xl scale-150" />
                  <div className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-full border-2 border-orange-500/40 bg-gradient-to-br from-orange-500/20 to-transparent flex items-center justify-center shadow-lg shadow-orange-500/15">
                    <UserRound className="w-10 h-10 sm:w-12 sm:h-12 text-orange-400" aria-hidden="true" />
                  </div>
                </div>
                <p className="font-heading font-bold text-lg sm:text-xl text-white">
                  Your missing team member
                </p>
                <p className="text-[11px] font-mono text-gray-500 mt-1 tracking-wider">
                  TRAINED ON YOUR BUSINESS
                </p>
              </div>

              <div className="space-y-3" data-testid="secretary-capabilities">
                {SECRETARY_CAPABILITIES.map((cap, index) => {
                  const isHero = cap.highlight;

                  return (
                    <motion.div
                      key={cap.id}
                      initial={reduceMotion ? false : { opacity: 0, x: -8 }}
                      animate={
                        reduceMotion || isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: -8 }
                      }
                      transition={{ duration: 0.35, delay: 0.15 + index * 0.08 }}
                      className={`flex items-center gap-4 rounded-xl border px-4 py-3.5 ${
                        isHero
                          ? "border-orange-500/40 bg-orange-500/10"
                          : "border-white/[0.06] bg-white/[0.02]"
                      }`}
                      data-testid={`secretary-mechanism-${index}`}
                    >
                      <span className="badge-mono text-[10px] text-gray-600 flex-shrink-0">
                        {cap.tag}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                          <span
                            className={`font-mono font-bold text-sm sm:text-base ${
                              isHero ? "text-orange-400" : "text-teal-400"
                            }`}
                          >
                            {cap.value}
                          </span>
                          <span className="font-heading font-semibold text-xs sm:text-sm text-white">
                            {cap.label}
                          </span>
                        </div>
                        <p className="text-[10px] sm:text-[11px] text-gray-500 font-body mt-0.5">
                          {cap.detail}
                        </p>
                      </div>
                      {isHero && (
                        <span className="badge-mono text-[8px] text-orange-400/90 tracking-wider flex-shrink-0 hidden sm:block">
                          KEY
                        </span>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </motion.div>

          <div className="hidden lg:block lg:col-span-3">
            <SideList data={SECRETARY_HIRED} variant="hired" />
          </div>

          <div className="grid grid-cols-2 gap-4 lg:hidden">
            <SideList data={SECRETARY_REJECTED} variant="rejected" />
            <SideList data={SECRETARY_HIRED} variant="hired" />
          </div>
        </div>

        <div
          className="rounded-xl border border-white/[0.06] bg-white/[0.015] overflow-hidden mb-8"
          data-testid="secretary-workflow"
        >
          <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-white/[0.06]">
            {SECRETARY_WORKFLOW.map((item, index) => (
              <div
                key={item.step}
                className="flex items-center gap-3 px-4 sm:px-5 py-4 relative"
              >
                {index < SECRETARY_WORKFLOW.length - 1 && (
                  <ArrowRight
                    className="hidden sm:block absolute -right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-orange-500/40 z-10"
                    aria-hidden="true"
                  />
                )}
                <span className="badge-mono text-[10px] font-bold text-orange-400/90 tracking-wider flex-shrink-0">
                  {item.step}
                </span>
                <span className="text-[11px] sm:text-xs text-gray-400 font-body leading-snug">
                  {item.text}
                </span>
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
          <div data-testid="secretary-outcome-callout">
            <p className="font-heading font-semibold text-sm sm:text-base text-[#F3F4F6] mb-1.5">
              {SECRETARY_PAYOFF.lead}
            </p>
            <p className="text-[11px] sm:text-xs font-mono text-gray-500">{SECRETARY_PAYOFF.channels}</p>
          </div>

          <button
            type="button"
            onClick={onOpenSignup}
            className="group inline-flex items-center justify-center gap-2 border border-orange-500/35 text-[#F3F4F6] font-heading font-semibold text-sm py-3 px-6 rounded-lg hover:border-orange-500/60 hover:bg-orange-500/5 transition-all duration-300 flex-shrink-0"
            data-testid="secretary-demo-link"
          >
            {SOFT_CTA}
            <ArrowRight className="w-4 h-4 text-orange-400 group-hover:translate-x-0.5 transition-transform" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default SecretarySection;
